<section class="branding-page template-editor-page">
  <!-- Standard 76px edge-to-edge topbar matching Branding Figma -->
  <header class="page-topbar hero-section branding-topbar template-editor-topbar">
    <div class="topbar-title-block page-header__title">
      <h1 class="page-title">Manage Brands</h1>
      <p class="page-subtitle helper-copy">
        Customize emails and document signing pages with your brand colors, logos, and more.
      </p>
    </div>

    <div class="page-header__actions">
      <button type="button" class="btn btn-secondary ghost-btn" (click)="goBack()">
        Back
      </button>

      @if (isDirty) {
        <button
          type="button"
          class="btn btn-secondary ghost-btn btn-secondary--danger"
          (click)="resetToDefault()"
          [disabled]="resetting || saving"
        >
          @if (resetting) {
            <span class="btn-spinner" aria-hidden="true"></span>
            <span>Resetting…</span>
          } @else {
            <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M1 4v6h6"></path>
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
            </svg>
            <span>Reset to default</span>
          }
        </button>

        <button
          type="button"
          class="btn btn-primary primary-btn btn-purple"
          (click)="saveChanges()"
          [disabled]="saving || resetting"
        >
          @if (saving) {
            <span class="btn-spinner" aria-hidden="true"></span>
            <span>Saving…</span>
          } @else {
            <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="4 10 8 14 16 6"></polyline>
            </svg>
            <span>Save changes</span>
          }
        </button>
      }
    </div>
  </header>

  <!-- Page Body with Compact Professional Spacing -->
  <div class="branding-body template-editor-body">
    <!-- Subheader Row: Back Button + Title -->
    <div class="template-editor-subnav-row">
      <button
        type="button"
        class="template-editor-back-btn header-icon"
        (click)="goBack()"
        aria-label="Back to email templates"
        title="Back to email templates"
      >
        <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="16" y1="10" x2="4" y2="10"></line>
          <polyline points="10 4 4 10 10 16"></polyline>
        </svg>
      </button>

      <div class="subnav-title-wrap">
        <h2 class="template-editor-subheading">{{ editorTitle }}</h2>
        @if (brand?.name) {
          <span class="brand-chip">{{ brand?.name }}</span>
        }
      </div>
    </div>

    @if (loading) {
      <div class="c-loading-state" aria-live="polite">
        <span class="c-spinner"></span>
        <p class="c-loading-text state-message">Loading template details…</p>
      </div>
    } @else if (errorMessage && !details) {
      <div class="alert-banner error" role="alert">
        <p class="state-message state-message--error">{{ errorMessage }}</p>
      </div>
    } @else if (details && currentDetail) {
      <div class="editor-layout">
        <!-- Left Column: Template Editor Card -->
        <section class="editor-pane editor-card">
          <div class="editor-note-row">
            <div class="editor-note-banner">
              <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="10" cy="10" r="9"></circle>
                <line x1="10" y1="9" x2="10" y2="15"></line>
                <circle cx="10" cy="5.5" r="0.5" fill="currentColor"></circle>
              </svg>
              <p class="editor-note">Note: Use / or @ to insert placeholders into the subject or body.</p>
            </div>

            @if (hasMultipleRecipientTypes) {
              <label class="recipient-select">
                <span class="recipient-label">Recipient</span>
                <select
                  class="recipient-dropdown"
                  [ngModel]="selectedRecipientType"
                  (ngModelChange)="onRecipientTypeChange($event)"
                >
                  @for (recipientType of details.recipientTypes; track recipientType) {
                    <option [value]="recipientType">{{ recipientType }}</option>
                  }
                </select>
              </label>
            }
          </div>

          @if (placeholderMenuTarget) {
            <div class="placeholder-menu" role="listbox" aria-label="Available placeholders">
              <div class="placeholder-menu-header">Available Placeholders</div>
              @for (option of filteredPlaceholderOptions; track option.token) {
                <button type="button" class="placeholder-menu__item" (click)="insertPlaceholder(option)">
                  <span class="placeholder-label">{{ option.label }}</span>
                  <code class="placeholder-token">{{ option.token }}</code>
                </button>
              }
            </div>
          }

          @if (errorMessage) {
            <div class="alert-banner error" role="alert">
              <p class="inline-error">{{ errorMessage }}</p>
            </div>
          }

          <!-- Field 1: Email Subject -->
          <section class="field-block">
            <label class="field-label" for="emailSubject">Email Subject</label>
            <input
              #subjectInput
              id="emailSubject"
              type="text"
              class="brand-input"
              [(ngModel)]="draftSubject"
              (input)="subjectErrorMessage = ''"
              (keyup)="onSubjectKeyup()"
              (click)="onSubjectSelectionChange()"
              (focus)="onSubjectSelectionChange()"
              placeholder="Enter email subject"
            />
            @if (subjectErrorMessage) {
              <p class="field-error" role="alert">{{ subjectErrorMessage }}</p>
            }
          </section>

          <!-- Field 2: Email Body with Rich Text Toolbar -->
          <section class="field-block">
            <div class="field-label-row">
              <label class="field-label">Email Body</label>
              <span
                class="status-pill c-status-pill"
                [class.is-customized]="currentStatus === 'Customized'"
                [class.status-completed]="currentStatus === 'Customized'"
              >
                <span class="c-status-dot"></span>
                {{ currentStatus }}
              </span>
            </div>

            <div class="editor-toolbar-wrapper">
              <div class="editor-toolbar" role="toolbar" aria-label="Email body formatting">
                <button type="button" class="toolbar-btn" (click)="applyBodyCommand('bold')" title="Bold">
                  <strong>B</strong>
                </button>
                <button type="button" class="toolbar-btn" (click)="applyBodyCommand('italic')" title="Italic">
                  <em>I</em>
                </button>
                <button type="button" class="toolbar-btn" (click)="applyBodyCommand('underline')" title="Underline">
                  <u>U</u>
                </button>
                <div class="toolbar-divider" aria-hidden="true"></div>
                <button type="button" class="toolbar-btn" (click)="applyBodyCommand('justifyLeft')" title="Align Left">
                  <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="3" y1="5" x2="17" y2="5"></line>
                    <line x1="3" y1="10" x2="11" y2="10"></line>
                    <line x1="3" y1="15" x2="15" y2="15"></line>
                  </svg>
                </button>
                <button type="button" class="toolbar-btn" (click)="applyBodyCommand('justifyCenter')" title="Align Center">
                  <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="3" y1="5" x2="17" y2="5"></line>
                    <line x1="6" y1="10" x2="14" y2="10"></line>
                    <line x1="4" y1="15" x2="16" y2="15"></line>
                  </svg>
                </button>
                <div class="toolbar-divider" aria-hidden="true"></div>

                <label class="toolbar-select">
                  <span class="sr-only">Font size</span>
                  <select [ngModel]="activeFontSize" (ngModelChange)="applyFontSize($event)" title="Font Size">
                    @for (option of fontSizeOptions; track option.value) {
                      <option [value]="option.value">{{ option.label }}</option>
                    }
                  </select>
                </label>

                <label class="toolbar-color" title="Text Color">
                  <span class="color-letter-a">A</span>
                  <input
                    type="color"
                    [ngModel]="activeTextColor"
                    (ngModelChange)="applyTextColor($event)"
                    aria-label="Text color"
                  />
                </label>
                <div class="toolbar-divider" aria-hidden="true"></div>

                <button type="button" class="toolbar-btn" (click)="applyBodyCommand('undo')" title="Undo">↶</button>
                <button type="button" class="toolbar-btn" (click)="applyBodyCommand('redo')" title="Redo">↷</button>
              </div>

              <div
                #bodyEditor
                class="body-editor"
                contenteditable="true"
                spellcheck="true"
                (input)="onBodyInput()"
                (keyup)="onBodyKeyup()"
                aria-label="Email body editor"
              ></div>
            </div>

            @if (bodyErrorMessage) {
              <p class="field-error" role="alert">{{ bodyErrorMessage }}</p>
            }
          </section>
        </section>

        <!-- Right Column: Sticky Live Preview Card -->
        <aside class="preview-pane">
          <div class="preview-shell preview-card-container">
            <div class="preview-header-row">
              <h3 class="preview-shell__label preview-title">Preview</h3>
            </div>

            <div class="preview-card">
              <div class="preview-stage" [style.background]="brand?.emailBackgroundColor || '#f8fafc'">
                <div class="preview-branding">
                  @if (brand?.logoDataUrl) {
                    <img [src]="brand?.logoDataUrl" [alt]="brand?.name || 'Brand logo'" class="preview-brand-logo" />
                  } @else {
                    <div class="preview-mark" [style.background]="brand?.primaryButtonColor || '#7E21D9'">
                      {{ previewBrandInitials }}
                    </div>
                  }
                  <strong class="preview-brand-title">{{ brand?.name || 'Your Brand' }}</strong>
                </div>

                <div class="preview-email-card">
                  <div class="preview-email-card__body" [innerHTML]="previewBodyHtml"></div>
                </div>

                @if (brand?.showPlatformBranding ?? true) {
                  <div class="preview-footer">Powered by XlhireEsigns</div>
                }
              </div>
            </div>
          </div>
        </aside>
      </div>
    }
  </div>
</section>
